import React, { type FunctionComponent, useMemo, useState } from 'react'
import {
  Button,
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  NewSelect,
  Text,
} from '@retailer-platform/shared-components'
import { useDomainMessages } from '../../utils/domain/intl.ts'
import { Property, PropertySection, PropertyTitle } from '../../utils/common.styles.tsx'
import { PropertyContainer } from '../shared-styled-components/SharedStyledComponents.tsx'
import { FeatureConfigKey } from '../../types/qualityAuditWorkflows.types.ts'
import { QualityAuditWorkflowConfigColumnModalFeatureSelect } from './components/QualityAuditWorkflowConfigColumnModalFeatureSelect.tsx'
import { QualityAuditWorkflowConfigColumnModalFeatureInput } from './components/QualityAuditWorkflowConfigColumnModalFeatureInput.tsx'

interface Props {
  featureConfig: { name: string; value: string }
  onClose: () => void
  onSave: (name: string, value: string) => void
}

export const QualityAuditWorkflowConfigFeatureModal: FunctionComponent<
  React.PropsWithChildren<Props>
> = ({ onClose, onSave, featureConfig }) => {
  const [featureConfigState, setFeatureConfigState] = useState<{
    name: string
    value: string
  }>(featureConfig)

  const i18n = useDomainMessages({
    title: 'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.title',
    nameTitle: 'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.name.label',
    nameDetails: 'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.name.details',
    valueTitle: 'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.value.label',
    valueDetails: 'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.value.details',
    save: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.save',
    close: 'catalogAdminDomain.qualityAuditWorkflows.configs.modal.close',
  })

  const options = useMemo(() => {
    const featureOptions = Object.values(FeatureConfigKey)
      .map(value => ({
        label: `${value}`,
        value: `${value}`,
      }))
      .sort((a, b) => (a?.label ?? '').localeCompare(b?.label ?? ''))

    if (!(featureConfigState?.name in FeatureConfigKey)) {
      featureOptions.push({
        label: featureConfigState?.name,
        value: featureConfigState?.name,
      })
    }

    return featureOptions
  }, [featureConfigState?.name])
  return (
    <Modal maxWidth={'70%'} maxHeight={'90%'} isOpen={true} hideCloseButton={true}>
      <ModalContainer>
        <ModalHeader>{i18n.title}</ModalHeader>
        <ModalBody>
          <PropertyContainer style={{ flexDirection: 'column' }}>
            <PropertySection style={{ flexDirection: 'row' }}>
              <Property style={{ flex: 1 }}>
                <PropertyTitle>{i18n.nameTitle}</PropertyTitle>
                <Text size="small" css={{ flex: 1 }}>
                  {i18n.nameDetails}
                </Text>
                <NewSelect
                  isClearable
                  // Handles user selecting value
                  onChange={value => {
                    setFeatureConfigState({
                      name: value ?? '',
                      value: featureConfigState.value,
                    })
                  }}
                  options={options}
                  // Handles user manually entering value
                  onInputChange={value => {
                    const cleanedValue = (value ?? '').trim().toLowerCase()
                    if (cleanedValue.length <= 0) {
                      return
                    }
                    setFeatureConfigState({
                      name: cleanedValue,
                      value: featureConfigState.value,
                    })
                  }}
                  value={featureConfigState?.name ?? ''}
                  isLoading={false}
                  components={{ Option: QualityAuditWorkflowConfigColumnModalFeatureSelect }}
                  isSearchable={true}
                />
              </Property>
            </PropertySection>
            <PropertySection style={{ flexDirection: 'row' }}>
              <Property style={{ flex: 1 }}>
                <PropertyTitle>{i18n.valueTitle}</PropertyTitle>
                <QualityAuditWorkflowConfigColumnModalFeatureInput
                  featureName={featureConfigState.name}
                  featureValue={`${featureConfigState.value}`}
                  onChange={value => {
                    setFeatureConfigState({
                      name: featureConfigState.name,
                      value: value.length > 1000 ? featureConfigState.value : value,
                    })
                  }}
                />
              </Property>
            </PropertySection>
          </PropertyContainer>
        </ModalBody>
        <ModalFooter>
          <Button variant={'secondary'} onClick={() => onClose()}>
            {i18n.close}
          </Button>
          <Button
            variant={'primary'}
            disabled={(featureConfigState?.value ?? '').length === 0}
            onClick={() => onSave(featureConfigState.name, featureConfigState.value)}
          >
            {i18n.save}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}
